import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { deposits, type Deposit } from "@/api/deposits";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

const STATUS_FILTERS = ["", "PENDING", "SUCCEEDED", "FAILED", "REFUNDED"];

/**
 * Admin deposit queue. Deposits normally settle automatically via the
 * processor webhook; this view lets operators monitor the flow and manually
 * resolve deposits stuck in PENDING (dev/MOCK, out-of-band confirmation,
 * reconciliation). Manual confirm reuses the webhook crediting path so
 * balance + ledger + treasury stay consistent.
 */
export function AdminDepositsPage() {
  const [status, setStatus] = useState("");
  const list = useQuery({
    queryKey: ["deposits", "admin", status],
    queryFn: () => deposits.adminAll(status || undefined),
    refetchInterval: 20_000,
  });
  const [failing, setFailing] = useState<Deposit | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const confirm = useMutation({
    mutationFn: (d: Deposit) => deposits.confirm(d.id),
    onSuccess: () => {
      toast.push("Deposit credited", "success");
      qc.invalidateQueries({ queryKey: ["deposits"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Deposits</h1>
        <div className="w-44">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s || "All statuses"}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Deposit flow</span>
            <Badge tone="neutral">{list.data?.length ?? 0}</Badge>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState title="No deposits" description="Client deposits appear here as they're initiated." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Created</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Amount</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Processor</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Account</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((d) => (
                  <tr key={d.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {d.created_at ? new Date(d.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">
                      {d.amount} {d.currency}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{d.processor}</td>
                    <td className="px-3 py-1.5">
                      {statusBadge(d.status)}
                      {d.failure_reason && (
                        <div className="italic text-term-mute">› {d.failure_reason}</div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {d.trading_account_id ? d.trading_account_id.slice(0, 8) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {d.status === "PENDING" && (
                        <div className="flex justify-end gap-1">
                          <Button
                            onClick={() => confirm.mutate(d)}
                            disabled={!d.processor_ref || confirm.isPending}
                            title={d.processor_ref ? "Credit this deposit" : "No processor reference"}
                          >
                            Confirm
                          </Button>
                          <Button variant="danger" onClick={() => setFailing(d)}>
                            Fail
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {failing && <FailModal row={failing} onClose={() => setFailing(null)} />}
    </div>
  );
}

function statusBadge(s: string) {
  const tone =
    s === "SUCCEEDED" ? "good"
      : s === "PENDING" ? "warn"
        : s === "FAILED" || s === "CANCELED" ? "bad"
          : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}

function FailModal({ row, onClose }: { row: Deposit; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => deposits.fail(row.id, reason || undefined),
    onSuccess: () => {
      toast.push("Deposit marked failed", "warn");
      qc.invalidateQueries({ queryKey: ["deposits"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title="Mark deposit failed">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          submit.mutate();
        }}
        className="space-y-3"
      >
        <div className="rounded-sm border border-term-border bg-term-elev px-2 py-1.5 text-xs text-term-dim">
          <span className="text-term-mute">Amount</span>{" "}
          <span className="font-mono text-term-fg">{row.amount} {row.currency}</span>
        </div>
        <div>
          <Label>Reason</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="optional" />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" loading={submit.isPending}>
            Mark failed
          </Button>
        </div>
      </form>
    </Modal>
  );
}
